import { wait } from '@change-corgi/core/async';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type { useStartAPetitionPageState } from 'src/app/pages/startAPetitionV2/pageContext';

import { getAsyncPetitionData } from './api/getAsyncPetitionData';
import type { SapCreatePetitionMutation, SapCreatePetitionMutationVariables } from './createPublishedPetition.graphql';
import { SAPCreatePetition } from './createPublishedPetition.graphql';
import { SapPetitionSaveError } from './errors';
import { schema } from './schema';
import { pollUntil } from './utils';

type CreatePublishedPetitionInput = NonNullable<
	Exclude<ReturnType<typeof useStartAPetitionPageState>['data']['draft'], 'input' | 'metadata'>
>;

type CreatePublishedPetitionParams = {
	petition: CreatePublishedPetitionInput;
	isKickstartExperimentEnabled: boolean;
};
type CreatePublishedPetitionReturn = Extract<
	SapCreatePetitionMutation['createPublishedPetition'],
	{ __typename: 'CreatePublishedPetitionSuccess' }
>['petition'] & {
	aiAsk?: string | null | undefined;
	aiDescription: string | null | undefined;
};

export async function createPublishedPetition(
	{ petition, isKickstartExperimentEnabled }: CreatePublishedPetitionParams,
	utilityContext: UtilityContext,
): Promise<CreatePublishedPetitionReturn> {
	try {
		const { data: petitionInput, error: validationError } = schema.safeParse(petition);

		// creating a new error with a custom message instead of using ZodError so we can easily search in logs
		if (validationError) throw new SapPetitionSaveError('InvalidInput', { cause: validationError });

		const [{ data }] = await Promise.all([
			utilityContext.gql.fetch<SapCreatePetitionMutation, SapCreatePetitionMutationVariables>({
				query: SAPCreatePetition,
				variables: {
					input: petitionInput,
				},
				rejectOnError: true,
			}),
			// minimum wait time accounts for displaying the loading screen
			wait(5000),
		]);

		if (data.createPublishedPetition.__typename !== 'CreatePublishedPetitionSuccess') {
			const typename = data.createPublishedPetition.__typename;
			throw new SapPetitionSaveError(typename);
		}

		const petitionId = data.createPublishedPetition.petition.id;
		await pollUntil(
			async () =>
				getAsyncPetitionData(
					{
						petitionId,
						fetchStarterFreePromotion: isKickstartExperimentEnabled,
					},
					utilityContext,
				),
			{
				enabled: isKickstartExperimentEnabled,
				interval: 1000,
				timeout: 5000,
			},
		);

		return {
			...data.createPublishedPetition.petition,
			aiAsk: petitionInput.aiAsk,
			aiDescription: petitionInput.aiDescription,
		};
	} catch (err) {
		const error = err instanceof SapPetitionSaveError ? err : new SapPetitionSaveError(undefined, { cause: err });
		void utilityContext.errorReporter.report({ error });
		throw error;
	}
}
